import {
  Activity,
  Heart,
  Zap,
  Moon,
  Calendar,
  Brain,
  RefreshCw,
  Droplets,
  Wind,
  Thermometer,
} from "lucide-react";
import welcomeBg from "@/projects/Admin_Bite_Balance/bite-balance/src/assets/welcome-bg.jpg";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Calendar as CalendarComponent } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/popover";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useState } from "react";

const Fitness = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date()
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Simulate Fitbit data sync
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1500);
  };

  const activityMetrics = [
    {
      icon: Activity,
      label: "Steps",
      value: "8,206",
      unit: "of 10,000",
      progress: 82,
      color: "bg-chart-1",
      lightColor: "bg-primary-light",
      showProgress: true,
    },
    {
      icon: Heart,
      label: "Resting Heart Rate",
      value: "68",
      unit: "BPM",
      progress: 68,
      color: "bg-chart-5",
      lightColor: "bg-chart-5/30",
      showProgress: false,
    },
    {
      icon: Zap,
      label: "Calories Burned",
      value: "420",
      unit: "kcal",
      progress: 70,
      color: "bg-chart-3",
      lightColor: "bg-accent-light",
      showProgress: true,
    },
    {
      icon: Zap,
      label: "Active Minutes",
      value: "45",
      unit: "min",
      progress: 75,
      color: "bg-chart-2",
      lightColor: "bg-secondary-light",
      showProgress: true,
    },
  ];

  const weeklyActivity = [
    { day: "Mon", steps: 9200, progress: 92 },
    { day: "Tue", steps: 7800, progress: 78 },
    { day: "Wed", steps: 10500, progress: 100 },
    { day: "Thu", steps: 8900, progress: 89 },
    { day: "Fri", steps: 11200, progress: 100 },
    { day: "Sat", steps: 6500, progress: 65 },
    { day: "Sun", steps: 8206, progress: 82 },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden pb-24">
      {/* Blurred Background Image */}
      <div
        className="fixed inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />

      {/* Overlay for better contrast */}
      <div className="fixed inset-0 bg-background/70" />

      <div className="relative z-10 mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Today's Activity
            </h1>
            <p className="text-sm text-muted-foreground">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-xl border-border/50"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw
                className={`h-5 w-5 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 rounded-xl border-border/50"
                >
                  <Calendar className="h-5 w-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="z-50 w-auto bg-card p-0" align="end">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                  className="pointer-events-auto"
                />
                {selectedDate && (
                  <div className="border-t border-border bg-card p-3">
                    <p className="text-center text-xs text-muted-foreground">
                      Viewing activity for {format(selectedDate, "PPP")}
                    </p>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Main Activity Metrics */}
        <div className="grid grid-cols-2 gap-3">
          {activityMetrics.map((metric, i) => {
            const Icon = metric.icon;
            const routes = [
              "/fitness/steps",
              "/fitness/heart-rate",
              "/fitness/calories",
              "/fitness/active-minutes",
            ];
            return (
              <Card
                key={i}
                className={`rounded-xl border-0 p-4 shadow-sm ${metric.lightColor} cursor-pointer transition-shadow hover:shadow-md`}
                onClick={() => navigate(routes[i])}
              >
                <div
                  className={`h-10 w-10 rounded-xl ${metric.color}/20 mb-3 flex items-center justify-center`}
                >
                  <Icon
                    className={`h-5 w-5 ${metric.color.replace("bg-", "text-")}`}
                  />
                </div>
                <div
                  className={metric.showProgress ? "space-y-2" : "space-y-1"}
                >
                  <div className="space-y-0.5">
                    <div className="text-2xl font-black leading-tight text-foreground dark:text-white">
                      {metric.value}
                    </div>
                    <div className="text-xs font-bold text-foreground/80 dark:text-white/80">
                      {metric.unit}
                    </div>
                  </div>
                  {metric.showProgress && (
                    <Progress value={metric.progress} className="h-1.5" />
                  )}
                  <p className="text-xs font-bold text-foreground/80 dark:text-white/80">
                    {metric.label}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Sleep */}
        <Card
          className="cursor-pointer rounded-2xl border-0 p-5 shadow-sm transition-shadow hover:shadow-md"
          onClick={() => navigate("/fitness/sleep")}
        >
          <div className="flex items-center gap-4">
            <div className="bg-info/10 flex h-14 w-14 items-center justify-center rounded-xl">
              <Moon className="text-info h-7 w-7" />
            </div>
            <div>
              <h3 className="mb-1 font-semibold text-foreground">Sleep</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-foreground">8.5</span>
                <span className="text-sm text-muted-foreground">hrs</span>
              </div>
              <p className="text-xs text-muted-foreground">of 9 hrs goal</p>
            </div>
          </div>
        </Card>

        {/* Stress Score */}
        <Card
          className="from-warning/5 to-warning/10 cursor-pointer rounded-2xl border-0 bg-gradient-to-br p-5 shadow-sm transition-shadow hover:shadow-md"
          onClick={() => navigate("/fitness/stress-score")}
        >
          <div className="flex items-center gap-4">
            <div className="bg-warning/10 flex h-14 w-14 items-center justify-center rounded-xl">
              <Brain className="text-warning h-7 w-7" />
            </div>
            <div>
              <h3 className="mb-1 font-semibold text-foreground">
                Stress Score
              </h3>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-foreground">6.2</span>
                <span className="text-sm text-muted-foreground">/ 10</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Moderate stress
              </p>
            </div>
          </div>
        </Card>

        {/* Body Metrics */}
        <div className="grid grid-cols-3 gap-2">
          <Card className="rounded-xl border-0 p-3 text-center shadow-sm">
            <div className="text-xl font-bold text-foreground">68</div>
            <div className="mb-1 text-xs text-muted-foreground">kg</div>
            <div className="text-xs text-muted-foreground">Weight</div>
          </Card>
          <Card className="rounded-xl border-0 p-3 text-center shadow-sm">
            <div className="text-xl font-bold text-foreground">22.4</div>
            <div className="mb-1 text-xs text-muted-foreground">km</div>
            <div className="text-xs text-muted-foreground">Distance</div>
          </Card>
          <Card className="rounded-xl border-0 p-3 text-center shadow-sm">
            <div className="text-xl font-bold text-foreground">8</div>
            <div className="mb-1 text-xs text-muted-foreground">floors</div>
            <div className="text-xs text-muted-foreground">Climbed</div>
          </Card>
        </div>

        {/* Heart Rate Variability - Main Card */}
        <Card
          className="hover-scale relative cursor-pointer overflow-hidden rounded-2xl border-0 shadow-sm transition-all hover:shadow-lg"
          onClick={() => navigate("/fitness/hrv")}
        >
          {/* Background gradient */}
          <div className="from-chart-5/20 via-chart-5/10 absolute inset-0 bg-gradient-to-br to-transparent" />

          {/* Decorative circles */}
          <div className="bg-chart-5/10 absolute -right-8 -top-8 h-32 w-32 rounded-full blur-xl" />
          <div className="bg-chart-5/5 absolute -bottom-4 -right-4 h-24 w-24 rounded-full" />

          <div className="relative p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-chart-5/20 border-chart-5/30 flex h-12 w-12 items-center justify-center rounded-2xl border backdrop-blur-sm">
                  <Heart className="text-chart-5 h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground">
                    Heart Rate Variability
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Last night's reading
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-end justify-between">
              <div>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black text-foreground">
                    47
                  </span>
                  <span className="text-lg font-medium text-muted-foreground">
                    ms
                  </span>
                </div>
              </div>

              {/* Mini trend visualization */}
              <div className="flex h-12 items-end gap-1">
                {[35, 42, 38, 45, 41, 48, 47].map((val, i) => (
                  <div
                    key={i}
                    className="bg-chart-5/60 w-2 rounded-full transition-all"
                    style={{ height: `${(val / 60) * 100}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Weekly Summary */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold text-foreground">This Week</h3>
          </div>

          <div className="space-y-3">
            {weeklyActivity.map((day, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-8 text-sm font-medium text-muted-foreground">
                  {day.day}
                </span>
                <div className="flex-1">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">
                      {day.steps.toLocaleString()}
                    </span>
                    <span className="text-xs text-muted-foreground">steps</span>
                  </div>
                  <Progress value={day.progress} className="h-1.5" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Weekly Average
              </span>
              <span className="text-lg font-bold text-foreground">
                9,186 steps
              </span>
            </div>
          </div>
        </Card>

        {/* Health Vitals - SpO2, Breathing Rate, Skin Temp */}
        <div className="space-y-2">
          <h3 className="px-1 font-semibold text-foreground">Health Vitals</h3>
          <div className="grid grid-cols-3 gap-2">
            {/* SpO2 */}
            <Card
              className="cursor-pointer rounded-xl border-0 p-3 text-center shadow-sm transition-shadow hover:shadow-md"
              onClick={() => navigate("/fitness/spo2")}
            >
              <div className="bg-info/10 mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg">
                <Droplets className="text-info h-5 w-5" />
              </div>
              <div className="text-xl font-bold text-foreground">97%</div>
              <div className="text-xs text-muted-foreground">SpO2</div>
            </Card>

            {/* Breathing Rate */}
            <Card
              className="cursor-pointer rounded-xl border-0 p-3 text-center shadow-sm transition-shadow hover:shadow-md"
              onClick={() => navigate("/fitness/breathing-rate")}
            >
              <div className="bg-chart-2/10 mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg">
                <Wind className="text-chart-2 h-5 w-5" />
              </div>
              <div className="text-xl font-bold text-foreground">14</div>
              <div className="text-xs text-muted-foreground">Breaths/min</div>
            </Card>

            {/* Skin Temperature */}
            <Card
              className="cursor-pointer rounded-xl border-0 p-3 text-center shadow-sm transition-shadow hover:shadow-md"
              onClick={() => navigate("/fitness/skin-temp")}
            >
              <div className="bg-chart-3/10 mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg">
                <Thermometer className="text-chart-3 h-5 w-5" />
              </div>
              <div className="text-xl font-bold text-foreground">-0.1°</div>
              <div className="text-xs text-muted-foreground">Skin Temp</div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Fitness;
